import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { apiService } from '@/services/api'
import { logger } from '@/utils/logger'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  picture?: string
  role: string
  provider: 'google' | 'demo'
  createdAt?: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  loginWithGoogle: (googleCredential: string) => Promise<{ success: boolean; message?: string }>
  loginWithToken: (token: string, email?: string) => Promise<{ success: boolean }>
  loginDemo: (customEmail?: string) => Promise<{ success: boolean; message?: string }>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const isAuthenticated = !!user && !!token

  // Load token from localStorage on init; in production, NEVER seed demo sessions
  useEffect(() => {
    const bootstrap = async () => {
      const savedToken = localStorage.getItem('auth_token')
      const savedUser = localStorage.getItem('auth_user')

      if (savedToken && savedUser) {
        try {
          const parsed = JSON.parse(savedUser)
          // If a demo session was stored, clear it in production
          if ((import.meta as any).env?.PROD && parsed?.provider === 'demo') {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('auth_user')
          } else {
            setToken(savedToken)
            setUser(parsed)
            apiService.setToken(savedToken)
            verifyToken(savedToken)
            setIsLoading(false)
            return
          }
        } catch (error) {
          logger.error('Failed to parse saved user data', error as Error)
          localStorage.removeItem('auth_token')
          localStorage.removeItem('auth_user')
        }
      }
      // No stored session; do nothing (wait for explicit Google sign-in)
      setIsLoading(false)
    }

    bootstrap()
  }, [])

  const verifyToken = async (_tokenToVerify: string) => {
    try {
      // For Google auth, we'll skip server verification for now
      // In production, you'd verify the token with your backend
      logger.info('Token verification skipped for Google auth')
    } catch (error) {
      logger.error('Token verification failed', error as Error)
      logout()
    }
  }

  const loginWithGoogle = async (googleCredential: string): Promise<{ success: boolean; message?: string }> => {
    try {
      setIsLoading(true)
      
      // Decode the Google JWT token to get user info
      const payload = JSON.parse(atob(googleCredential.split('.')[1]))
      
      // Create user object from Google data
      const userData: User = {
        id: payload.sub,
        email: payload.email,  // Use real Google email
        firstName: payload.given_name || '',
        lastName: payload.family_name || '',
        picture: payload.picture,
        role: 'user',
        provider: 'google',
        createdAt: new Date().toISOString()
      }

      // Generate a simple token (in production, this would come from your backend)
      const authToken = `google_${payload.sub}_${Date.now()}`
      
      setUser(userData)
      setToken(authToken)
      
      // Save to localStorage
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('auth_user', JSON.stringify(userData))
      
      // Set token in API service for authenticated requests
      apiService.setToken(authToken)
      
      // Expose for early API init
      try { (window as any).__seed_api_token__ = authToken } catch {}

      logger.info('Google authentication successful', { email: userData.email })
      
      return { 
        success: true, 
        message: `Welcome ${userData.firstName}!` 
      }
    } catch (error) {
      logger.error('Google authentication failed', error as Error)
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Google authentication failed' 
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Direct token login (from backend OAuth redirect params)
  const loginWithToken = async (tokenFromBackend: string, email?: string): Promise<{ success: boolean }> => {
    try {
      setIsLoading(true)
      const userData: User = {
        id: email,
        email: email!,  // Email is required from backend
        firstName: (email || 'User').split('@')[0],
        lastName: '',
        role: 'user',
        provider: 'google',
        createdAt: new Date().toISOString()
      }
      setUser(userData)
      setToken(tokenFromBackend)
      localStorage.setItem('auth_token', tokenFromBackend)
      localStorage.setItem('auth_user', JSON.stringify(userData))
      apiService.setToken(tokenFromBackend)
      try { (window as any).__seed_api_token__ = tokenFromBackend } catch {}
      return { success: true }
    } finally {
      setIsLoading(false)
    }
  }

  const loginDemo = async (customEmail?: string): Promise<{ success: boolean; message?: string }> => {
    // Disable demo login in production (Vite env)
    if ((import.meta as any).env?.PROD) {
      return { success: false, message: 'Demo login disabled in production' }
    }
    try {
      setIsLoading(true)
      
      // Use custom email or default
      const email = customEmail || 'demo@dataraiils.com'
      const firstName = email.split('@')[0] || 'Demo'
      
      // Create demo user
      const userData: User = {
        id: 'demo-user',
        email: email,
        firstName: firstName,
        lastName: 'User',
        role: 'admin',
        provider: 'demo',
        createdAt: new Date().toISOString()
      }

      const authToken = `demo_${Date.now()}`
      
      setUser(userData)
      setToken(authToken)
      
      // Save to localStorage
      localStorage.setItem('auth_token', authToken)
      localStorage.setItem('auth_user', JSON.stringify(userData))
      
      // Set token in API service
      apiService.setToken(authToken)
      
      logger.info('Demo authentication successful')
      
      return { 
        success: true, 
        message: 'Demo login successful!' 
      }
    } catch (error) {
      logger.error('Demo authentication failed', error as Error)
      return { 
        success: false, 
        message: 'Demo login failed' 
      }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    try {
      // Clear state
      setUser(null)
      setToken(null)
      
      // Clear localStorage
      localStorage.removeItem('auth_token')
      localStorage.removeItem('auth_user')
      
      // Clear API service token
      apiService.setToken(null)
      
      logger.info('User logged out successfully')
    } catch (error) {
      logger.error('Logout failed', error as Error)
    }
  }

  const refreshUser = async (): Promise<void> => {
    try {
      if (!token) {
        throw new Error('No token available')
      }
      
      // For Google auth, we'll just keep the current user data
      // In production, you might refresh the Google token or fetch updated user data
      logger.info('User refresh completed')
    } catch (error) {
      logger.error('Failed to refresh user', error as Error)
      logout()
    }
  }

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    loginWithGoogle,
    loginWithToken,
    loginDemo,
    logout,
    refreshUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}